/**
 * Info Modal
 *
 * Full-screen modal overlay for displaying tool call history and working memory status.
 * Swipe down or tap X to close on mobile.
 */

import React from 'react';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  lastToolCall: string;
  workingMemoryLength: number;
}

const InfoModal: React.FC<InfoModalProps> = ({
  isOpen,
  onClose,
  lastToolCall,
  workingMemoryLength,
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="info-modal-backdrop"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="info-modal">
        {/* Header */}
        <div className="info-modal-header">
          <h3>System Information</h3>
          <button
            onClick={onClose}
            className="info-modal-close"
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="info-modal-content">
          {/* Working Memory Status */}
          <div className="info-section">
            <h4>Working Memory</h4>
            <p className="info-value">
              {workingMemoryLength > 0 ? (
                <>
                  <span className="status-dot active" />
                  {workingMemoryLength.toLocaleString()} characters loaded
                </>
              ) : (
                <>
                  <span className="status-dot" />
                  Not loaded
                </>
              )}
            </p>
          </div>

          {/* Last Tool Call */}
          <div className="info-section">
            <h4>Last Tool Call</h4>
            {lastToolCall ? (
              <pre className="tool-call-display">
                {lastToolCall}
              </pre>
            ) : (
              <p className="info-value muted">No tool calls yet</p>
            )}
          </div>

          {/* Legend */}
          <div className="info-section">
            <h4>Available Tools</h4>
            <ul className="tools-list">
              <li><code>search_memory</code> - Search past conversations</li>
              <li><code>get_working_memory</code> - Load current context snapshot</li>
              <li><code>get_open_loops</code> - View the Action Jackson list</li>
              <li><code>get_life_list</code> - List Life List items</li>
              <li><code>log_decision</code> - Record a decision</li>
              <li><code>log_open_loop</code> - Add to the Action Jackson list</li>
              <li><code>resolve_open_loop</code> - Resolve an Action Jackson list item</li>
              <li><code>update_open_loop</code> - Change an Action Jackson list item</li>
              <li><code>abandon_open_loop</code> - Archive an Action Jackson list item without deleting history</li>
              <li><code>reopen_open_loop</code> - Reopen an Action Jackson list item</li>
              <li><code>add_life_list_item</code> - Add an item to Life List</li>
              <li><code>update_life_list_item</code> - Change or mark a Life List item done</li>
              <li><code>remove_life_list_item</code> - Remove a Life List item</li>
              <li><code>promote_life_list_item</code> - Move or copy a Life List item into the Action Jackson list</li>
              <li><code>reverse_life_list_promotion</code> - Undo a Life List promotion</li>
              <li><code>add_principle</code> - Add guiding principle/rule</li>
              <li><code>flag_for_review</code> - Flag items needing review</li>
              <li><code>propose_writeback</code> - Stage external write-back action</li>
              <li><code>commit_writeback</code> - Approve/reject staged write-back</li>
              <li><code>get_conversation</code> - Retrieve full conversation</li>
              <li><code>refresh_working_memory</code> - Update context</li>
              <li><code>googleSearch</code> - Search the web through Gemini grounding</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default InfoModal;
